"use client";

import { ReactNode, createContext, useContext } from "react";

import { formatDate } from "@/lib/format-data";
import {
  computeTrendDirection,
  formatChange,
  getTrendColor,
  getTrendIcon,
} from "@/lib/measurement-calculations";
import { cx } from "@/lib/tailwind-utils";

import type { MeasurementField } from "@/types/measurement";

type DeltaSize = "xs" | "sm";

const SIZE_CLASS: Record<DeltaSize, string> = {
  xs: "text-caption",
  sm: "text-body",
};

type DeltaDirection = ReturnType<typeof computeTrendDirection>;

interface DeltaContextValue {
  change: number | null;
  field: MeasurementField;
  direction: DeltaDirection;
  isEmpty: boolean;
  size: DeltaSize;
}

const DeltaContext = createContext<DeltaContextValue | null>(null);

function useDelta() {
  const ctx = useContext(DeltaContext);
  if (!ctx) {
    throw new Error("Delta.* components must be rendered inside <Delta>");
  }
  return ctx;
}

interface DeltaProps {
  change: number | null;
  field: MeasurementField;
  size?: DeltaSize;
  className?: string;
  children: ReactNode;
}

/**
 * Compound component for rendering a measurement delta. The wrapper sets
 * up shared context (change, field, direction, size, empty state) and
 * applies the trend color to its children. Compose with `<Delta.Icon>`,
 * `<Delta.Value>`, `<Delta.Empty>`, and `<Delta.Since>`.
 *
 * Empty state = `change == null`. `0` is treated as a real "neutral"
 * value (renders "No change" via formatChange, with the neutral icon).
 * Callers that want zero to read as empty (e.g. record dialog showing
 * `—`) should pass `change === 0 ? null : change` themselves.
 *
 * Layout (flex / inline / gap) is intentionally not baked in — pass it
 * via `className` so each call site keeps its existing structure.
 */
function Delta({
  change,
  field,
  size = "xs",
  className,
  children,
}: DeltaProps) {
  const isEmpty = change == null;
  const direction = isEmpty
    ? null
    : computeTrendDirection(change, field.displayConfig?.trend?.higherIsBetter);

  return (
    <DeltaContext.Provider value={{ change, field, direction, isEmpty, size }}>
      <span
        className={cx(
          SIZE_CLASS[size],
          isEmpty
            ? "text-foreground-muted"
            : cx("font-medium", getTrendColor(direction)),
          className,
        )}
      >
        {children}
      </span>
    </DeltaContext.Provider>
  );
}

/** Trend icon (↑ / ↓ / —). Renders nothing when empty. */
function DeltaIcon() {
  const { isEmpty, direction } = useDelta();
  if (isEmpty || !direction) return null;
  return <>{getTrendIcon(direction)}</>;
}

/** Formatted change value (e.g. `+1.2 lbs`). Renders nothing when empty. */
function DeltaValue() {
  const { change, field, isEmpty } = useDelta();
  if (isEmpty || change == null) return null;
  return <>{formatChange(change, field)}</>;
}

/** Renders its children only when the delta is empty (change is null). */
function DeltaEmpty({ children }: { children: ReactNode }) {
  const { isEmpty } = useDelta();
  if (!isEmpty) return null;
  return <>{children}</>;
}

/** Muted "since MMM d, yyyy" suffix; renders in both empty and non-empty cases. */
function DeltaSince({
  date,
  className,
}: {
  date: Date | string;
  className?: string;
}) {
  const { size } = useDelta();
  return (
    <span
      className={cx(
        "font-normal text-foreground-muted",
        SIZE_CLASS[size],
        className,
      )}
    >
      since {formatDate(date)}
    </span>
  );
}

Delta.Icon = DeltaIcon;
Delta.Value = DeltaValue;
Delta.Empty = DeltaEmpty;
Delta.Since = DeltaSince;

export { Delta };
